// ─── Generic filter / sort / aggregate engine ───────────────────────────────
//
// Generalized from `AcquisitionGridService.ts`'s dataKey-driven fallback
// branch. Bespoke per-column logic (the equivalent of that file's long
// if/else chain) is supplied by the consuming table via
// `ColumnMeta.specialCase` / `ColumnMeta.getValue`, rather than hardcoded
// here.

import {
  ColumnMeta,
  ColumnMetaMap,
  DATE_LASTDAYS_FILTER_SUFFIX,
  DATE_RANGE_FILTER_SUFFIX,
  FilterValue,
  isEnumFilterValue,
  isRangeFilterValue
} from './types';

// Fallback formatters for aggregate totals on columns that declared no
// explicit `formatAggregate` (see `computeColumnAggregates`).
/**
 * Returns a formatter function that formats a number as a USD currency string (via toLocaleString), taking a `maxDepth` sentinel parameter.
 *
 * @param maxDepth - Depth level, used only as a type guard variant (1, 2, or 3). Not used internally.
 * @returns A function that formats a number as a USD currency string, e.g. "$1,234,567.89".
 *
 * @example
 * CURRENCY_AGGREGATE_FORMATTER(1)(1234.50) // returns '$1,234.50'
 */
const CURRENCY_AGGREGATE_FORMATTER = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
/**
 * Returns a formatter function that formats a number as a US-decimal string (via toLocaleString), taking a `maxDepth` sentinel parameter.
 *
 * @param maxDepth - Depth level, used only as a type guard variant (1, 2, or 3). Not used internally.
 * @returns A function that formats a number as a US-decimal string with thousand separators, e.g. "1,234,567.89".
 *
 * @example
 * NUMBER_AGGREGATE_FORMATTER(1)(1234.50) // returns '1,234.50'
 */
const NUMBER_AGGREGATE_FORMATTER = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

/** Dot-notation path accessor (identical in spirit to TransactionTable's `getNestedValue`). */
export function getNestedValue(obj: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc != null && typeof acc === 'object') {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

/**
 * Resolves a column's raw value for a row, using the column's `getValue` accessor if present, otherwise its `dataKey` dot-path, falling back to the raw column key.
 */
function resolveValue<TRow, K extends string>(row: TRow, meta: ColumnMeta<TRow, K> | undefined, columnKey: string): unknown {
  if (!meta) return undefined;
  if (meta.getValue) return meta.getValue(row);
  if (meta.dataKey) return getNestedValue(row, meta.dataKey);
  return getNestedValue(row, columnKey);
}

/** Distinct string filter option values for an enum-filterable column, derived across the current row set. */
export function getEnumFilterOptions<TRow, K extends string>(
  rows: TRow[],
  columnKey: K,
  columnMeta: ColumnMetaMap<TRow, K>,
): string[] {
  const meta = columnMeta[columnKey];
  if (meta?.getFilterOptions) return meta.getFilterOptions(rows);
  const values = new Set<string>();
  for (const row of rows) {
    const raw = resolveValue(row, meta, columnKey);
    if (raw != null && raw !== '') values.add(String(raw));
  }
  return Array.from(values).sort((a, b) => a.localeCompare(b));
}

/** Per-option row counts for an enum-filterable column, derived across the current row set — powers the count shown next to each filter option. */
export function getEnumFilterCounts<TRow, K extends string>(
  rows: TRow[],
  columnKey: K,
  columnMeta: ColumnMetaMap<TRow, K>,
): Record<string, number> {
  const meta = columnMeta[columnKey];
  const counts: Record<string, number> = {};
  for (const row of rows) {
    const raw = resolveValue(row, meta, columnKey);
    if (raw == null || raw === '') continue;
    const value = String(raw);
    counts[value] = (counts[value] ?? 0) + 1;
  }
  return counts;
}

/**
 * Numeric [min, max] domain for a range-filterable column, derived across
 * the current row set — used to bound the range-filter slider. Returns
 * `null` if the column has no numeric values in the current row set.
 */
export function getNumericDomain<TRow, K extends string>(
  rows: TRow[],
  columnKey: K,
  columnMeta: ColumnMetaMap<TRow, K>,
): { min: number; max: number } | null {
  const meta = columnMeta[columnKey];
  let min = Infinity;
  let max = -Infinity;
  for (const row of rows) {
    const raw = resolveValue(row, meta, columnKey);
    const num = typeof raw === 'number' ? raw : Number(raw);
    if (Number.isNaN(num)) continue;
    if (num < min) min = num;
    if (num > max) max = num;
  }
  if (min === Infinity || max === -Infinity) return null;
  return { min, max };
}

/**
 * Checks whether a raw value matches an enum/multi-select filter, comparing case-insensitively against the list of selected filter values.
 */
function matchesEnumFilter(rawValue: unknown, filterValues: string[]): boolean {
  const itemValue = rawValue != null ? String(rawValue).toLowerCase() : '';
  return filterValues.map((v) => v.toLowerCase()).includes(itemValue);
}

/**
 * Checks whether a raw value falls within a numeric [min, max] range filter, returning false if the value isn't numeric.
 */
function matchesRangeFilter(rawValue: unknown, range: { min?: number; max?: number }): boolean {
  const num = typeof rawValue === 'number' ? rawValue : Number(rawValue);
  if (Number.isNaN(num)) return false;
  if (range.min != null && num < range.min) return false;
  if (range.max != null && num > range.max) return false;
  return true;
}

function matchesLastDaysFilter(rawValue: unknown, days: number): boolean {
  const value = typeof rawValue === 'number' ? rawValue : Number(rawValue);
  if (Number.isNaN(value)) return false;
  const now = Date.now();
  return value >= now - days * 24 * 60 * 60 * 1000 && value <= now;
}

/**
 * Filters `rows` against `filters` (column key -> FilterValue). Empty/absent
 * filter entries are ignored. A `specialCase.filter` resolver, if present for
 * a column, takes precedence over the generic dataKey-driven fallback.
 */
export function filterGridItems<TRow, K extends string>(
  rows: TRow[],
  filters: Record<string, FilterValue>,
  columnMeta: ColumnMetaMap<TRow, K>,
): TRow[] {
  const activeFilters = Object.entries(filters).filter(([, v]) => {
    if (isEnumFilterValue(v)) return v.length > 0;
    if (isRangeFilterValue(v)) return v.min != null || v.max != null;
    return false;
  });
  if (activeFilters.length === 0) return rows;

  return rows.filter((row) =>
    activeFilters.every(([columnKey, filterValue]) => {
      const meta = columnMeta[columnKey as K];
      if (columnKey.endsWith(DATE_RANGE_FILTER_SUFFIX)) {
        const baseKey = columnKey.slice(0, -DATE_RANGE_FILTER_SUFFIX.length);
        const baseMeta = columnMeta[baseKey as K];
        const rawValue = resolveValue(row, baseMeta, baseKey);
        if (isRangeFilterValue(filterValue)) return matchesRangeFilter(rawValue, filterValue);
      }
      if (columnKey.endsWith(DATE_LASTDAYS_FILTER_SUFFIX)) {
        const baseKey = columnKey.slice(0, -DATE_LASTDAYS_FILTER_SUFFIX.length);
        const baseMeta = columnMeta[baseKey as K];
        const rawValue = resolveValue(row, baseMeta, baseKey);
        if (isEnumFilterValue(filterValue) && filterValue[0]) return matchesLastDaysFilter(rawValue, Number(filterValue[0]));
      }
      if (meta?.specialCase?.filter) return meta.specialCase.filter(row, filterValue);

      const rawValue = resolveValue(row, meta, columnKey);
      if (isRangeFilterValue(filterValue)) return matchesRangeFilter(rawValue, filterValue);
      return matchesEnumFilter(rawValue, filterValue);
    }),
  );
}

/**
 * Filters `rows` against one or more `GroupConfig` selections (e.g. Division,
 * Management Type quick-filter cards). Each group axis is AND-ed with the
 * others; within a group axis, selected options are OR-ed (matches any).
 */
export function filterGridItemsByGroups<TRow, K extends string>(
  rows: TRow[],
  groupFilters: Record<string, string[]>,
  groupFieldByGroupId: Record<string, K>,
  columnMeta: ColumnMetaMap<TRow, K>,
): TRow[] {
  const activeGroups = Object.entries(groupFilters).filter(([, values]) => values.length > 0);
  if (activeGroups.length === 0) return rows;

  return rows.filter((row) =>
    activeGroups.every(([groupId, selected]) => {
      const columnKey = groupFieldByGroupId[groupId];
      const meta = columnMeta[columnKey];
      const rawValue = resolveValue(row, meta, columnKey);
      return matchesEnumFilter(rawValue, selected);
    }),
  );
}

/**
 * Sorts `rows` by `sortColumns`/`sortOrders` (parallel arrays, same shape as
 * TransactionTable's `TablePreset.sortColumns`/`sortOrders`). A
 * `specialCase.sort` resolver, if present, takes precedence over the generic
 * dataKey/getValue-driven fallback.
 */
export function sortGridItems<TRow, K extends string>(
  rows: TRow[],
  sortColumns: string[],
  sortOrders: ('asc' | 'desc' | 'none')[],
  columnMeta: ColumnMetaMap<TRow, K>,
): TRow[] {
  if (sortColumns.length === 0 || sortOrders[0] === 'none') return rows;
  const sorted = [...rows];
  sorted.sort((a, b) => {
    for (let i = 0; i < sortColumns.length; i++) {
      const columnKey = sortColumns[i] as K;
      const order = sortOrders[i];
      if (order === 'none') continue;
      const meta = columnMeta[columnKey];

      if (meta?.specialCase?.sort) {
        const comparison = meta.specialCase.sort(a, b, order);
        if (comparison !== 0) return comparison;
        continue;
      }

      const aValue = resolveValue(a, meta, columnKey);
      const bValue = resolveValue(b, meta, columnKey);

      const aNum = typeof aValue === 'number' ? aValue : Number(aValue);
      const bNum = typeof bValue === 'number' ? bValue : Number(bValue);
      if (!Number.isNaN(aNum) && !Number.isNaN(bNum) && aValue !== '' && bValue !== '' && aValue != null && bValue != null) {
        const comparison = order === 'asc' ? aNum - bNum : bNum - aNum;
        if (comparison !== 0) return comparison;
        continue;
      }

      const aStr = aValue != null ? String(aValue) : '';
      const bStr = bValue != null ? String(bValue) : '';
      const comparison = order === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
      if (comparison !== 0) return comparison;
    }
    return 0;
  });
  return sorted;
}

/**
 * When `true`, a column that declares no `ColumnMeta.aggregate` is treated as
 * `'sum'` — but only if EVERY non-blank value in the given row set is numeric
 * (so a text/enum/date column is never accidentally summed). Used for
 * group-row subtotals (`groupGridRows`), so a numeric column shows a per-group
 * total without each table having to opt every numeric column in explicitly.
 * The footer row (`computeColumnAggregates` with this off) is unaffected —
 * there, only columns with an explicit `aggregate` produce a total.
 */
function resolveAggregateKind<TRow, K extends string>(
  meta: ColumnMeta<TRow, K>,
  key: K,
  rows: TRow[],
  defaultNumericSum: boolean,
): 'sum' | 'avg' | 'count' | undefined {
  if (meta.aggregate) return meta.aggregate;
  if (!defaultNumericSum) return undefined;
  // isDate columns store epoch-ms numbers but are conceptually not summable.
  if (meta.isDate) return undefined;
  let sawValue = false;
  for (const row of rows) {
    const raw = resolveValue(row, meta, key);
    if (raw == null || raw === '') continue;
    sawValue = true;
    if (typeof raw !== 'number' && Number.isNaN(Number(raw))) return undefined;
  }
  return sawValue ? 'sum' : undefined;
}

/**
 * Computes the raw numeric aggregate for every column declaring
 * `ColumnMeta.aggregate`, over the given row set — the un-formatted values
 * `computeColumnAggregates` formats for display, also used directly by
 * `sortGroupedBuckets` to order group buckets numerically rather than by
 * comparing already-formatted strings (e.g. `"$1,234"`).
 *
 * @param defaultNumericSum - See `resolveAggregateKind`. Defaults to `false`
 *   (footer semantics: only explicit `aggregate` columns produce a value).
 */
export function computeColumnAggregateValues<TRow, K extends string>(
  rows: TRow[],
  columnMeta: ColumnMetaMap<TRow, K>,
  defaultNumericSum = false,
): Partial<Record<K, number>> {
  const result: Partial<Record<K, number>> = {};

  for (const key of Object.keys(columnMeta) as K[]) {
    const meta = columnMeta[key];
    const kind = resolveAggregateKind(meta, key, rows, defaultNumericSum);
    if (!kind) continue;

    const numericValues: number[] = [];
    for (const row of rows) {
      const raw = resolveValue(row, meta, key);
      const num = typeof raw === 'number' ? raw : Number(raw);
      if (!Number.isNaN(num)) numericValues.push(num);
    }

    if (kind === 'count') {
      result[key] = numericValues.length;
    } else if (kind === 'sum') {
      result[key] = numericValues.reduce((acc, v) => acc + v, 0);
    } else {
      result[key] = numericValues.length > 0 ? numericValues.reduce((acc, v) => acc + v, 0) / numericValues.length : 0;
    }
  }

  return result;
}

/**
 * Computes footer-row aggregates for every column declaring `ColumnMeta.aggregate`,
 * over the full (already filtered) row set — never just a virtualized/visible window.
 */
export function computeColumnAggregates<TRow, K extends string>(
  rows: TRow[],
  columnMeta: ColumnMetaMap<TRow, K>,
  defaultNumericSum = false,
): Partial<Record<K, string>> {
  const values = computeColumnAggregateValues(rows, columnMeta, defaultNumericSum);
  const result: Partial<Record<K, string>> = {};

  for (const key of Object.keys(values) as K[]) {
    const meta = columnMeta[key];
    const value = values[key]!;
    if (meta.formatAggregate) {
      result[key] = meta.formatAggregate(value);
    } else if (meta.isCurrency) {
      // Auto-summed group totals (defaultNumericSum) on a currency column
      // that declared no explicit formatAggregate still read as currency,
      // rather than a bare number.
      result[key] = CURRENCY_AGGREGATE_FORMATTER.format(value);
    } else {
      result[key] = NUMBER_AGGREGATE_FORMATTER.format(Math.round(value * 100) / 100);
    }
  }

  return result;
}

/** Sentinel bucket label used when a row's group-by value is missing/blank. */
export const UNGROUPED_BUCKET_LABEL = '—';

/**
 * One group ("Row Crop", "California", ...) produced by `groupGridRows` — a
 * single collapsible summary row and its member rows.
 */
export interface GroupedRowBucket<TRow, K extends string> {
  /** The group's raw value as a string — also its identity/React key. */
  key: string;
  /** Display label for the group header row. Currently identical to `key`. */
  label: string;
  /** Member rows of this group, in first-appearance order. */
  rows: TRow[];
  /** Per-column footer-style subtotal for this group's rows, via `computeColumnAggregates`. */
  aggregates: Partial<Record<K, string>>;
  /** Same subtotals as `aggregates`, as raw unformatted numbers — used by `sortGroupedBuckets`. */
  aggregateValues: Partial<Record<K, number>>;
}

/**
 * Buckets an already filtered/sorted row set by the string value of
 * `groupByKey` (resolved the same way filtering/sorting resolve column
 * values — `ColumnMeta.getValue`, then `ColumnMeta.dataKey`, then the raw
 * column key), preserving each bucket's first-appearance order in `rows`
 * (so bucket order follows the current sort rather than being
 * re-alphabetized). Rows with a missing/blank value are grouped under
 * `UNGROUPED_BUCKET_LABEL`, or `ColumnMeta.emptyGroupLabel` when the
 * group-by column declares one. Each bucket gets its own per-column
 * aggregate via `computeColumnAggregates` with `defaultNumericSum` on — so
 * every column whose values are numeric shows a per-group SUM even if it
 * declared no explicit `ColumnMeta.aggregate` (columns with an explicit
 * `aggregate`/`formatAggregate` still use those; text/date/enum columns
 * stay blank). This differs from the footer row, where only columns with an
 * explicit `aggregate` produce a total. Bucket order can subsequently be
 * re-ordered via `sortGroupedBuckets` to reflect an active column sort.
 */
export function groupGridRows<TRow, K extends string>(
  rows: TRow[],
  groupByKey: K,
  columnMeta: ColumnMetaMap<TRow, K>,
): GroupedRowBucket<TRow, K>[] {
  const meta = columnMeta[groupByKey];
  const emptyLabel = meta?.emptyGroupLabel ?? UNGROUPED_BUCKET_LABEL;
  const order: string[] = [];
  const buckets = new Map<string, TRow[]>();

  for (const row of rows) {
    const raw = resolveValue(row, meta, groupByKey);
    const key = raw != null && raw !== '' ? String(raw) : emptyLabel;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = [];
      buckets.set(key, bucket);
      order.push(key);
    }
    bucket.push(row);
  }

  return order.map((key) => {
    const bucketRows = buckets.get(key)!;
    return {
      key,
      label: key,
      rows: bucketRows,
      aggregates: computeColumnAggregates(bucketRows, columnMeta, true),
      aggregateValues: computeColumnAggregateValues(bucketRows, columnMeta, true),
    };
  });
}

/**
 * Re-orders group buckets (from `groupGridRows`) to reflect the table's
 * active multi-column sort, mirroring `sortGridItems`'s semantics but applied
 * to buckets instead of individual rows:
 *
 * - Sorting by the group-by column itself compares each bucket's `label`
 *   alphabetically (case-insensitive).
 * - Sorting by any other column compares that column's raw numeric
 *   `aggregateValues` subtotal for each bucket whenever one exists (an
 *   explicit `ColumnMeta.aggregate` OR an auto-summed numeric column — see
 *   `groupGridRows`); columns with no per-bucket subtotal fall back to
 *   comparing bucket labels.
 * - Multi-column sort falls through to the next sort column on ties, exactly
 *   like `sortGridItems`.
 *
 * Returns `buckets` unchanged (same reference) when no sort is active, so
 * bucket order stays in its default "first appearance" order from
 * `groupGridRows`.
 */
export function sortGroupedBuckets<TRow, K extends string>(
  buckets: GroupedRowBucket<TRow, K>[],
  groupByKey: K,
  sortColumns: string[],
  sortOrders: ('asc' | 'desc' | 'none')[],
  columnMeta: ColumnMetaMap<TRow, K>,
): GroupedRowBucket<TRow, K>[] {
  const activeSorts = sortColumns
    .map((key, i) => ({ key: key as K, order: sortOrders[i] }))
    .filter((s) => s.order && s.order !== 'none');

  // No active sort => nothing to reorder, so return the input untouched (same
  // reference) — buckets keep their "first appearance" order from
  // `groupGridRows`, and callers relying on reference identity for
  // memoization don't get a needless new array.
  if (activeSorts.length === 0) return buckets;

  const ordered = [...buckets];
  ordered.sort((a, b) => {
    for (const { key, order } of activeSorts) {
      let comparison: number;

      if (key === groupByKey) {
        comparison = a.label.toLowerCase().localeCompare(b.label.toLowerCase());
      } else if (a.aggregateValues[key] != null || b.aggregateValues[key] != null) {
        // A per-group subtotal exists for this column (either an explicit
        // `aggregate` or an auto-summed numeric column — see `groupGridRows`),
        // so sort buckets by that subtotal, matching what the group row shows.
        const aValue = a.aggregateValues[key] ?? 0;
        const bValue = b.aggregateValues[key] ?? 0;
        comparison = aValue - bValue;
      } else {
        comparison = a.label.toLowerCase().localeCompare(b.label.toLowerCase());
      }

      if (order === 'desc') comparison = -comparison;
      if (comparison !== 0) return comparison;
    }
    return 0;
  });
  return ordered;
}
